import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PROJECT_ACCENT_VAR } from "@/components/project/ProjectThemeProvider";
import type { GalleryVideo } from "./GalleryVideoCard";

interface GalleryNewVideoToastProps {
  video: GalleryVideo | null;
  onDismiss: () => void;
}

const AUTO_DISMISS_MS = 5_000;
const SCROLL_THRESHOLD = 100;

export default function GalleryNewVideoToast({
  video,
  onDismiss,
}: GalleryNewVideoToastProps) {
  const { t } = useTranslation("gallery");
  const shouldReduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const scrolledRef = useRef(false);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      scrolledRef.current = window.scrollY > SCROLL_THRESHOLD;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show toast when a new video arrives and user is scrolled down
  useEffect(() => {
    if (!video) {
      setVisible(false);
      return;
    }
    if (!scrolledRef.current) {
      // User is at the top — no toast needed
      onDismiss();
      return;
    }
    setVisible(true);
    dismissTimerRef.current = setTimeout(() => {
      setVisible(false);
      onDismiss();
    }, AUTO_DISMISS_MS);
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [video, onDismiss]);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setVisible(false);
    onDismiss();
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
  };

  return (
    <AnimatePresence>
      {visible && video && (
        <Box
          component={motion.div}
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={shouldReduceMotion ? undefined : { opacity: 0, y: -40 }}
          transition={
            shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: "easeOut" }
          }
          onClick={handleClick}
          sx={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(12px)",
            borderRadius: 99,
            borderLeft: `3px solid var(${PROJECT_ACCENT_VAR}, #3b82f6)`,
            px: 2,
            py: 1,
            cursor: "pointer",
            zIndex: 1300,
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.25)",
            },
          }}
        >
          {video.thumbnail_url && (
            <Box
              component="img"
              loading="lazy"
              src={video.thumbnail_url}
              alt=""
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {t("toast.newVideo")}
          </Typography>
        </Box>
      )}
    </AnimatePresence>
  );
}
